import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { quadraticCost } from "@voting/quadratic";
import { PrimaryButton, SecondaryLink, SkeletonBlock } from "../components/ui";
import { RankedBallot, defaultRankOrder, rankedPayloadFromOrder } from "../components/voting/RankedBallot";
import { QuadraticBallot, emptyQuadraticAlloc, quadraticPayloadFromAlloc } from "../components/voting/QuadraticBallot";
import { ResultsBoard } from "../components/voting/ResultsBoard";
import { ResultsRecap } from "../components/voting/ResultsRecap";
import { useRoomSocket } from "../hooks/useRoomSocket";
import { pollPhaseLabel, votingStyleLabel } from "../lib/phaseLabels";
import { isResultsPersonaId, type ResultsPersonaId } from "../lib/personaPreference";
import { webBallotCopy } from "../lib/webPersonaCopy";
import {
		companionBallotEyebrow,
		companionBallotVerifyFallback,
		companionHostLiveBadge,
		friendlyApiErrorPayload,
		friendlyRealtimeErrorMessage,
	} from "../lib/userFacingErrors";
import { phaseFromLastWsMessage } from "../lib/wsLive";
import { isFinalResults } from "../types/finalResults";

type Opt = { id: number; content: string };

type Snapshot = {
	source: string;
	d1: {
		roomId: string;
		question: string;
		votingType: string;
		phase: string;
		expiresAt: number;
		options: Opt[];
		finalResults?: unknown;
	};
	live: {
		roomId: string;
		question: string;
		votingType: string;
		phase: string;
		expiresAt: number;
		options: Opt[];
	} | null;
};

type WsSnapshot = { type: "snapshot"; role: string; payload: Snapshot["live"] };

type SlackBallotVerifyResponse = {
	ok?: boolean;
	companion?: boolean;
	votingOpen?: boolean;
	message?: string;
	snapshot?: {
		roomId: string;
		question: string;
		votingType: string;
		phase: string;
		expiresAt: number;
		options: Opt[];
	};
	error?: string;
};

export function RoomPage() {
	const { roomId } = useParams();
	const location = useLocation();
	const companionToken = useMemo(() => new URLSearchParams(location.search).get("st")?.trim() || null, [location.search]);
	const companionHost = useMemo(() => {
		const raw = new URLSearchParams(location.search).get("host")?.trim().toLowerCase();
		if (raw === "clickup") return "clickup";
		if (raw === "asana") return "asana";
		return "slack";
	}, [location.search]);
	const embedMode = useMemo(
		() => new URLSearchParams(location.search).get("embed") === "1",
		[location.search],
	);
	const ballotApiSegment = useMemo(() => {
		if (companionHost === "clickup") return "clickup-ballot";
		if (companionHost === "asana") return "asana-ballot";
		return "slack-ballot";
	}, [companionHost]);
		const companionMode = Boolean(companionToken);
		/** Dialect from curator invitation (`?persona=` mirrors room resultsPersona). No voter picker. */
		const personaId: ResultsPersonaId = useMemo(() => {
			const raw = new URLSearchParams(location.search).get("persona");
			return raw && isResultsPersonaId(raw) ? raw : "neutral";
		}, [location.search]);
		const [snap, setSnap] = useState<Snapshot | null>(null);
	const [error, setError] = useState<string | null>(null);

	const [tally, setTally] = useState<{ voteCount: number; perOption: Record<string, number> } | null>(null);
	const [gatherBounce, setGatherBounce] = useState(false);
	const prevVoteCountRef = useRef<number | null>(null);
	const [presence, setPresence] = useState(0);
	const [castDone, setCastDone] = useState(false);
	const [voteErr, setVoteErr] = useState<string | null>(null);
	const [casting, setCasting] = useState(false);

	const [rankOrder, setRankOrder] = useState<number[]>([]);
	const [quadAlloc, setQuadAlloc] = useState<Record<number, number>>({});
	const [rankGestureOk, setRankGestureOk] = useState(false);
	const [votingEpoch, setVotingEpoch] = useState(0);

	const prevPhaseRef = useRef<string | undefined>(undefined);
	const prevRevealPhaseRef = useRef<string | undefined>(undefined);
	const [animateReveal, setAnimateReveal] = useState(false);

	/** Gathering — scale-bounce when vote count ticks up via WS (PRD §3.3). */
	useEffect(() => {
		const next = tally?.voteCount;
		if (typeof next !== "number") return;
		const prev = prevVoteCountRef.current;
		prevVoteCountRef.current = next;
		if (prev === null || next <= prev) return;
		setGatherBounce(true);
		const t = window.setTimeout(() => setGatherBounce(false), 220);
		return () => window.clearTimeout(t);
	}, [tally?.voteCount]);

	const wsFrameRef = useRef<((msg: unknown) => void) | undefined>(undefined);
	const { connected, upgradeRejected, lastEvent, send } = useRoomSocket(
		roomId,
		"voter",
		Boolean(roomId) && !companionMode,
		wsFrameRef,
	);
	const [companionVotingOpen, setCompanionVotingOpen] = useState(true);

	const loadSnap = async () => {
		if (!roomId) return;
		const res = await fetch(`/api/rooms/${encodeURIComponent(roomId)}`);
		const data = (await res.json()) as Snapshot & { error?: string };
		if (!res.ok)
			throw new Error(
				friendlyApiErrorPayload(
					data as unknown,
					"We couldn’t open this poll. Check the link or ask your curator for a new one.",
				),
			);
		setSnap(data as Snapshot);
	};

	const loadCompanionBallot = async () => {
		if (!roomId || !companionToken) return;
		const res = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/${ballotApiSegment}/verify`, {
			method: "POST",
			headers: { "content-type": "application/json" },
			body: JSON.stringify({ token: companionToken }),
		});
		const data = (await res.json()) as SlackBallotVerifyResponse;
		if (!res.ok) {
			throw new Error(
				friendlyApiErrorPayload(
					data as unknown,
					typeof data.message === "string"
						? data.message
						: companionBallotVerifyFallback(companionHost),
				),
			);
		}
		const snapPayload = data.snapshot;
		if (!snapPayload) throw new Error("Could not load this poll.");
		setCompanionVotingOpen(data.votingOpen !== false);
		setSnap({
			source: `${companionHost}-ballot`,
			d1: { ...snapPayload, finalResults: undefined },
			live: snapPayload,
		});
	};

	useEffect(() => {
		if (!roomId) return;
		let cancelled = false;
		(async () => {
			try {
				if (companionMode) await loadCompanionBallot();
				else await loadSnap();
			} catch (e) {
				if (!cancelled)
					setError(
						e instanceof Error
							? e.message
							: "We couldn’t open this poll. Check the link or ask your curator for a new one.",
					);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [roomId, companionMode, companionToken, ballotApiSegment]);

	const fromWs =
		lastEvent && typeof lastEvent === "object" && lastEvent !== null && (lastEvent as { type?: string }).type === "snapshot"
			? (lastEvent as WsSnapshot).payload
			: null;

	const effective = fromWs ?? snap?.live ?? snap?.d1;
	const livePhase = phaseFromLastWsMessage(lastEvent);
	const phase = livePhase ?? effective?.phase ?? "collecting";

	const options = effective?.options ?? [];
	const optionSig = useMemo(() => options.map((o) => o.id).join(","), [options]);

	wsFrameRef.current = (msg: unknown) => {
		if (!msg || typeof msg !== "object") return;
		const m = msg as { type?: string; code?: string; message?: string; voteCount?: number; perOption?: Record<string, number>; connected?: number };
		if (m.type === "tally_update" && typeof m.voteCount === "number" && m.perOption) {
			setTally({ voteCount: m.voteCount, perOption: m.perOption });
		}
		if (m.type === "presence" && typeof m.connected === "number") {
			setPresence(m.connected);
		}
		if (m.type === "vote_accepted") {
			setCastDone(true);
			setCasting(false);
			setVoteErr(null);
		}
		if (m.type === "error") {
			if (
				m.code === "vote_rejected" ||
				(typeof m.message === "string" && m.message.toLowerCase().includes("vote"))
			) {
				const raw =
					typeof m.message === "string" && m.message.length > 0
						? m.message
						: typeof m.code === "string"
							? m.code
							: "";
				setVoteErr(friendlyRealtimeErrorMessage(raw));
				setCasting(false);
			}
		}
	};

	useEffect(() => {
		if (phase !== "revealed" || !roomId) return;
		let cancelled = false;
		void (async () => {
			try {
				const res = await fetch(`/api/rooms/${encodeURIComponent(roomId)}`);
				const data = (await res.json()) as Snapshot & { error?: string };
				if (!cancelled && res.ok) setSnap(data as Snapshot);
			} catch {
				// ignore
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [phase, roomId]);

		useEffect(() => {
			const was = prevPhaseRef.current;
			prevPhaseRef.current = phase;
			if (phase !== "voting" || !options.length) return;
			if (was === "voting") return;

			setRankOrder(defaultRankOrder(options));
			setQuadAlloc(emptyQuadraticAlloc(options));
			setCastDone(false);
			setVoteErr(null);
			setTally(null);
			setRankGestureOk(false);
			setVotingEpoch((e) => e + 1);
		}, [phase, optionSig, options]);

		useEffect(() => {
			const was = prevRevealPhaseRef.current;
			prevRevealPhaseRef.current = phase;
			if (phase === "revealed" && was != null && was !== "revealed") {
				setAnimateReveal(true);
			}
		}, [phase]);

	useEffect(() => {
		if (!connected || !roomId) return;
		const t = window.setInterval(() => {
			send({ type: "presence_ping" });
		}, 28_000);
		return () => window.clearInterval(t);
	}, [connected, roomId, send]);

	const quadStrings = useMemo(() => {
		const r: Record<string, number> = {};
		for (const [k, v] of Object.entries(quadAlloc)) r[String(k)] = v;
		return r;
	}, [quadAlloc]);

	const quadSpent = useMemo(() => {
		try {
			return quadraticCost(quadStrings);
		} catch {
			return 0;
		}
	}, [quadStrings]);

	const quadEngaged = quadSpent > 0;

	const finalResults = snap?.d1?.finalResults;
	const results = isFinalResults(finalResults) ? finalResults : null;

	const linkLive = companionMode || connected;
	const canCastRanked =
		phase === "voting" &&
		!castDone &&
		linkLive &&
		(companionMode ? companionVotingOpen : true) &&
		effective?.votingType === "ranked" &&
		rankGestureOk &&
		rankOrder.length === options.length;
	const canCastQuad =
		phase === "voting" &&
		!castDone &&
		linkLive &&
		effective?.votingType === "quadratic" &&
		quadEngaged &&
		!companionMode;

	const rankedDisplayOrder = useMemo(() => {
		if (!options.length) return [];
		if (rankOrder.length !== options.length) return defaultRankOrder(options);
		return rankOrder;
	}, [options, rankOrder]);

	const canCast =
		phase === "voting" &&
		!castDone &&
		linkLive &&
		(effective?.votingType === "ranked" ? canCastRanked : effective?.votingType === "quadratic" ? canCastQuad : false);

	const handleCastVote = useCallback(() => {
		if (!effective || casting) return;
		if (phase !== "voting" || castDone || !linkLive) return;
		if (effective.votingType === "ranked" && !canCastRanked) return;
		if (effective.votingType === "quadratic" && !canCastQuad) return;

		setCasting(true);
		setVoteErr(null);
		if (companionMode && companionToken && effective.votingType === "ranked") {
			const ord = rankOrder.length === options.length ? rankOrder : rankedDisplayOrder;
			void (async () => {
				try {
					const res = await fetch(`/api/rooms/${encodeURIComponent(roomId!)}/${ballotApiSegment}/vote`, {
						method: "POST",
						headers: { "content-type": "application/json" },
						body: JSON.stringify({
							token: companionToken,
							vote: rankedPayloadFromOrder(ord, options),
						}),
					});
					const data = (await res.json()) as { error?: string; message?: string };
					if (!res.ok) {
						setVoteErr(
							friendlyApiErrorPayload(
								data as unknown,
								typeof data.message === "string" ? data.message : "Your ballot could not be recorded.",
							),
						);
						setCasting(false);
						return;
					}
					setCastDone(true);
					setCasting(false);
					setVoteErr(null);
				} catch {
					setVoteErr("Your ballot could not be recorded. Try again.");
					setCasting(false);
				}
			})();
			return;
		}
		if (effective.votingType === "ranked") {
			const ord = rankOrder.length === options.length ? rankOrder : rankedDisplayOrder;
			send({ type: "cast_vote", vote: rankedPayloadFromOrder(ord, options) });
		} else {
			send({ type: "cast_vote", vote: quadraticPayloadFromAlloc(quadAlloc) });
		}
	}, [
		effective,
		casting,
		phase,
		castDone,
		linkLive,
		canCastRanked,
		canCastQuad,
		rankOrder,
		options.length,
		rankedDisplayOrder,
		options,
		quadAlloc,
		send,
		companionMode,
		companionToken,
		roomId,
		ballotApiSegment,
	]);

	const ballotCopy = useMemo(
		() => webBallotCopy(personaId, roomId ?? "web", effective?.question ?? ""),
		[personaId, roomId, effective?.question],
	);
	const showVotingUi = phase === "voting" && !castDone;
	const showWaiting = phase === "voting" && castDone;
	const showCollecting = phase === "collecting";

	useEffect(() => {
		if (!showVotingUi || !canCast || casting) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "Enter" || (!e.ctrlKey && !e.metaKey)) return;
			const t = e.target as HTMLElement | null;
			if (t?.closest("textarea, input, select, [contenteditable='true']")) return;
			e.preventDefault();
			handleCastVote();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [showVotingUi, canCast, casting, handleCastVote]);

	if (!roomId) {
		return <p className="text-wi-text/70">This link is missing the poll ID. Open the invitation link from your curator.</p>;
	}

	if (error) {
		return (
			<div className="space-y-4">
				<h1 className="font-display text-3xl text-wi-text">Poll</h1>
				<p className="text-wi-gold" role="alert">
					{error}
				</p>
				<Link to="/" className="text-wi-gold text-sm underline-offset-2 hover:underline">
					Back home
				</Link>
			</div>
		);
	}

	if (!snap || !effective) {
		return (
			<div className="space-y-md" role="status" aria-live="polite" aria-busy="true">
				<span className="sr-only">Loading poll…</span>
				<SkeletonBlock className="h-3 w-24" />
				<SkeletonBlock className="h-8 w-3/4 max-w-md" />
				<SkeletonBlock className="h-4 w-full max-w-lg" />
				<SkeletonBlock className="h-4 w-5/6 max-w-md" />
				<div className="space-y-2 pt-sm">
					<SkeletonBlock className="h-12 w-full rounded-xl" />
					<SkeletonBlock className="h-12 w-full rounded-xl" />
					<SkeletonBlock className="h-12 w-full rounded-xl" />
				</div>
			</div>
		);
	}

		return (
			<div className={`space-y-md ${embedMode ? "pb-6" : "pb-16"}`}>
				<div className="flex flex-wrap items-end justify-between gap-sm">
					<div>
						<p className="font-medium text-caption text-wi-gold uppercase tracking-[0.2em]">
							{companionMode
								? embedMode && companionHost === "clickup"
									? "ClickUp task ballot"
									: companionBallotEyebrow(companionHost)
								: ballotCopy.eyebrow}
						</p>
						<h1 className={`font-display text-wi-text ${embedMode ? "text-h2" : "text-h1"}`}>
							{effective.question}
						</h1>
					</div>
					<div className="flex flex-col items-end gap-1.5">
						<span
							role="status"
							className={`text-caption font-medium ${linkLive ? "text-wi-signal" : "text-wi-text/55"}`}
						>
							{companionMode ? companionHostLiveBadge(companionHost) : connected ? "Live" : "Reconnecting…"}
						</span>
						{upgradeRejected ? (
							<p className="max-w-xs text-right text-wi-gold/90 text-caption leading-snug">
								Live updates could not open. What you see may be a moment behind — try refreshing.
							</p>
						) : null}
					</div>
				</div>

				<p className="text-caption text-wi-text/55">
					<span className="font-medium text-wi-text/70">{pollPhaseLabel(phase)}</span>
					<span aria-hidden="true"> · </span>
					{votingStyleLabel(effective.votingType)}
					<span aria-hidden="true"> · </span>
					Closes {new Date(effective.expiresAt).toLocaleString()}
					{presence > 0 ? (
						<>
							<span aria-hidden="true"> · </span>
							<span aria-label={`${presence} connected`}>{presence} connected</span>
						</>
					) : null}
					{tally && phase === "voting" ? (
						<>
							<span aria-hidden="true"> · </span>
							<span className={gatherBounce ? "wi-gather-bounce" : undefined}>
								{tally.voteCount} vote{tally.voteCount === 1 ? "" : "s"} so far
							</span>
						</>
					) : null}
					</p>

					{showCollecting ? (
					<p className="text-body-sm text-wi-text/80">
						{ballotCopy.collectingHint ||
							"The Curator has not opened voting yet. Keep this page open; it will update when voting starts."}{" "}
						<Link to="/help/running-a-session" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Running a session
						</Link>
					</p>
				) : null}

				{showVotingUi && !companionMode ? (
					<p className="text-body-sm text-wi-text/75 leading-relaxed">{ballotCopy.intro}</p>
				) : null}

			{showVotingUi && effective.votingType === "ranked" ? (
				<div className="relative">
					<a
						href="#wi-cast-vote"
						className="sr-only rounded-lg bg-wi-gold/15 px-3 py-2 font-medium text-wi-text text-sm underline underline-offset-2 focus:not-sr-only focus:absolute focus:left-0 focus:top-0 focus:z-20 focus:inline-block focus:outline-none focus:ring-2 focus:ring-wi-gold/45"
					>
						Skip to cast vote
					</a>
					<RankedBallot
						key={`rk-${votingEpoch}`}
						options={options}
						order={rankedDisplayOrder}
						onOrderChange={setRankOrder}
						disabled={!linkLive || casting}
						ballotResetKey={votingEpoch}
						onGestureEligibleChange={setRankGestureOk}
					/>
				</div>
			) : null}

			{showVotingUi && effective.votingType === "quadratic" ? (
				<div className="relative">
					<a
						href="#wi-cast-vote"
						className="sr-only rounded-lg bg-wi-gold/15 px-3 py-2 font-medium text-wi-text text-sm underline underline-offset-2 focus:not-sr-only focus:absolute focus:left-0 focus:top-0 focus:z-20 focus:inline-block focus:outline-none focus:ring-2 focus:ring-wi-gold/45"
					>
						Skip to cast vote
					</a>
					<QuadraticBallot
						key={`qd-${votingEpoch}`}
						options={options}
						alloc={quadAlloc}
						onAllocChange={setQuadAlloc}
						disabled={!linkLive || casting}
					/>
				</div>
			) : null}

				{showVotingUi ? (
					<div id="wi-cast-vote" className="relative scroll-mt-28 space-y-sm">
						<div className="wi-cta-row">
							<PrimaryButton disabled={!canCast || casting} onClick={handleCastVote}>
								{casting ? ballotCopy.castingLabel : ballotCopy.submitLabel}
							</PrimaryButton>
						</div>
						{effective.votingType === "ranked" ? (
							<span className="block text-caption text-wi-text/55">
								Reorder with drag, arrows on a row, <strong className="font-medium text-wi-text/70">J / K</strong> between
								rows, <strong className="font-medium text-wi-text/70">1–9</strong> to jump rank, then{" "}
								<strong className="font-medium text-wi-text/70">{ballotCopy.submitLabel}</strong> or{" "}
								<strong className="font-medium text-wi-text/70">⌘/Ctrl+Enter</strong>.
							</span>
						) : (
							<span className="block text-caption text-wi-text/55">
								Large +/− targets; focus a row and use <strong className="font-medium text-wi-text/70">+ / −</strong> keys.
								Spend at least one point before casting (⌘/Ctrl+Enter when ready).
							</span>
						)}
					</div>
				) : null}

				{voteErr ? (
					<p className="text-wi-gold text-body-sm" role="alert">
						{voteErr}
					</p>
				) : null}

				{showWaiting ? (
					<div className="space-y-sm py-sm text-center">
						<p className="font-display text-h2 text-wi-text">{ballotCopy.waitingHeadline}</p>
						<p className="text-body-sm text-wi-text/70">{ballotCopy.waitingBody}</p>
						{tally ? (
							<p className="text-body-sm text-wi-text/65" aria-live="polite">
								<span className={gatherBounce ? "wi-gather-bounce font-medium text-wi-text" : "font-medium text-wi-text"}>
									{tally.voteCount}
								</span>{" "}
								vote{tally.voteCount === 1 ? "" : "s"} gathered so far
							</p>
						) : null}
						<div className="flex justify-center pt-xs">
							<SecondaryLink
								arrow={false}
								onClick={() => {
									setCastDone(false);
									setVoteErr(null);
								}}
							>
								Change my vote
							</SecondaryLink>
						</div>
					</div>
				) : null}

				{phase === "revealed" && results ? (
					<>
						<ResultsBoard results={results} animateReveal={animateReveal} />
						{roomId ? <ResultsRecap roomId={roomId} /> : null}
					</>
				) : null}

			{phase === "revealed" && !results ? (
				<p className="text-body-sm text-wi-text/70">
					This poll closed before we could load results. Refresh once, or ask the curator.
				</p>
			) : null}

			{embedMode ? null : (
				<SecondaryLink to="/" arrow={false}>
					← New poll
				</SecondaryLink>
			)}
		</div>
	);
}
